import type { UserTag } from '@metapic-apps/shared'
import { type Ref, ref, toValue, watchEffect } from 'vue'

import { normalizeUserTags } from '@/lib/adapters/user-tags.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { metapicApiClient } from '@/lib/services/metapic-api.service'
import { useErrorStore } from '@/stores/error'

export const useGetUserTagListRequest = (str: Ref<string>, skip?: boolean) => {
  const data = ref<UserTag[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  const errorStore = useErrorStore()
  // Search responses can arrive out of order; only the latest query may update shared state.
  let latestRequestId = 0

  const fetchUserTags = async () => {
    const requestId = ++latestRequestId
    try {
      data.value = []
      isLoading.value = true
      error.value = null
      const query = toValue(str).trim().toLowerCase()
      const response = await metapicApiClient.userTagsIndex({
        ...(query && { name: query }),
        'access_levels[]': ['0'],
        size: 20,
        sort_by: 'name:asc',
      })
      if (requestId === latestRequestId) {
        data.value = normalizeUserTags(response.data)
      }
    } catch (e: unknown) {
      if (requestId === latestRequestId) {
        errorStore.setError(handleErrorMessage(e))
        error.value = e as Error
      }
    } finally {
      if (requestId === latestRequestId) {
        isLoading.value = false
      }
    }
  }

  watchEffect(() => {
    if (!skip) {
      void fetchUserTags()
    }
  })

  return {
    data,
    isLoading,
    error,
  }
}
